/**
 * Phrases wallets use when the user declines a request. Privy, Phantom and
 * injected EVM wallets word it differently; any of them means the user chose
 * not to sign, not that something broke.
 */
const WALLET_REJECTION =
  /user rejected|user denied|rejected the request|request rejected|user cancel|cancelled by user|canceled by user|approval denied|plugin closed/i

/** EIP-1193's "user rejected request" code, which some Solana wallets reuse. */
const USER_REJECTED_CODE = 4001

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

/**
 * The first human-readable text an error carries. Wallet SDKs do not always
 * throw `Error`s — some reject with plain objects, or with an `Error` whose
 * message is an object turned into "[object Object]" — so look past `message`
 * to the fields they use instead.
 */
function readErrorText(error: unknown, depth = 0): string | undefined {
  if (typeof error === "string") return error.trim() || undefined
  if (!isRecord(error) || depth > 2) return undefined

  for (const key of ["message", "error", "reason", "details"]) {
    const value = error[key]
    if (typeof value === "string") {
      const text = value.trim()
      if (text && text !== "[object Object]") return text
    } else if (isRecord(value)) {
      const nested = readErrorText(value, depth + 1)
      if (nested) return nested
    }
  }

  return readErrorText(error.cause, depth + 1)
}

function isWalletRejection(error: unknown, text: string | undefined) {
  if (text && WALLET_REJECTION.test(text)) return true
  return isRecord(error) && error.code === USER_REJECTED_CODE
}

/**
 * What to show when a transaction does not go through: a plain sentence when
 * the user declined it in their wallet, the error's own text otherwise, and
 * never "[object Object]".
 */
export function getTransactionErrorMessage(error: unknown): string {
  const text = readErrorText(error)
  if (isWalletRejection(error, text)) return "Transaction was cancelled"
  return text ?? "Your wallet did not complete the transaction"
}

export enum TransactionEvent {
  Started = "Transaction started",
  Failed = "Transaction failed",
  Completed = "Transaction completed",
}
